import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useInterfaceStore, TipoEmpresa } from '../../stores/useInterfaceStore';
import api from '../../services/api';
import { BACKOFFICE_URL, podeAcessarBackoffice, usuarioLogadoEmail } from '../../utils/backoffice';

interface EmpresaOption {
  id: number;
  razao_social: string;
  nome_fantasia: string;
  tipo: TipoEmpresa;
}

const CompanySelection: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [empresas, setEmpresas] = useState<EmpresaOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setEmpresaId, setTipoEmpresa } = useInterfaceStore();

  const fetchEmpresas = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getCompanies();
      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        setEmpresas(response.data);
      } else if (response.success && Array.isArray(response.data) && response.data.length === 0) {
        setError('Nenhuma empresa disponível para este usuário. Verifique as permissões.');
      } else {
        const msg = (response.data as any)?.error || response.error || 'Erro ao carregar empresas.';
        setError(msg);
      }
    } catch (err: any) {
      setError(err?.message || 'Erro de conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchEmpresas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCompany = () => {
    if (!selectedCompany) return;
    const empresa = empresas.find(e => e.id === parseInt(selectedCompany));
    if (!empresa) return;

    setEmpresaId(empresa.id);
    setTipoEmpresa(empresa.tipo);
    navigate('/app/dashboard');
  };

  return (
    <Container fluid className="d-flex vh-100 justify-content-center align-items-center bg-light">
      <Row>
        <Col>
          <Card style={{ width: '30rem' }} className="p-4 shadow-sm">
            <Card.Body>
              <h1 className="h2 text-center mb-3">LGPD</h1>
              <h2 className="h4 text-center text-muted mb-4">Selecione a Empresa</h2>

              {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
              ) : error ? (
                <>
                  <Alert variant="danger" className="mb-3">
                    <strong>Erro ao carregar empresas:</strong><br />{error}
                  </Alert>
                  <div className="d-grid">
                    <Button variant="outline-primary" onClick={fetchEmpresas}>
                      Tentar novamente
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Form.Select
                    aria-label="Seleção de empresa"
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="mb-3"
                  >
                    <option value="" disabled>Selecione uma empresa</option>
                    {empresas.map((empresa) => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nome_fantasia || empresa.razao_social}
                        {empresa.tipo ? ` (${empresa.tipo})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="d-grid">
                    <Button variant="primary" onClick={handleSelectCompany} disabled={!selectedCompany}>
                      Entrar
                    </Button>
                  </div>
                </>
              )}

              {podeAcessarBackoffice(usuarioLogadoEmail()) && (
                <div className="d-grid mt-3 pt-3 border-top">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    href={BACKOFFICE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Acessar BackOffice
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CompanySelection;
